import React, { useState } from "react";
import {
    Box,
    Map as MapIcon,
    ArrowDownLeft,
    ArrowUpRight,
    Layout,
    Search,
    Plus,
    MoreHorizontal,
    ChevronRight,
    AlertCircle,
    ArrowRightLeft
} from "lucide-react";

export default function WMSManager() {
    const [activeTab, setActiveTab] = useState("map"); // map, suspense, locations
    const [selectedBin, setSelectedBin] = useState(null);

    // Mock data for warehouse layout
    const warehouseMap = [
        {
            aisle: "A1", sections: [
                { id: "A1-S1", bins: ["A1-S1-01", "A1-S1-02", "A1-S1-03"] },
                { id: "A1-S2", bins: ["A1-S2-01", "A1-S2-02", "A1-S2-03"] },
            ]
        },
        {
            aisle: "A2", sections: [
                { id: "A2-S1", bins: ["A2-S1-01", "A2-S1-02", "A2-S1-03"] },
                { id: "A2-S2", bins: ["A2-S2-01", "A2-S2-02", "A2-S2-03"] },
            ]
        },
    ];

    const suspenseItems = [
        { id: "SUS-001", sku: "TGL-882", qty: 45, receivedAt: "2026-02-25", client: "SME-Global" },
        { id: "SUS-002", sku: "TGL-104", qty: 12, receivedAt: "2026-02-26", client: "LogiTrans" },
    ];

    const renderWarehouseMap = () => (
        <div className="space-y-8">
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-navy-900 font-serif">Warehouse Floor Plan</h3>
                <div className="flex gap-2 text-xs">
                    <span className="flex items-center gap-1"><div className="w-3 h-3 bg-emerald-100 border border-emerald-200 rounded"></div> Optimal</span>
                    <span className="flex items-center gap-1"><div className="w-3 h-3 bg-amber-100 border border-amber-200 rounded"></div> Near Full</span>
                    <span className="flex items-center gap-1"><div className="w-3 h-3 bg-white border border-slate-200 rounded"></div> Empty</span>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                {warehouseMap.map((aisle) => (
                    <div key={aisle.aisle} className="bg-slate-50 p-6 rounded-2xl border border-slate-200">
                        <h4 className="text-lg font-bold text-navy-900 mb-4 px-2">Aisle {aisle.aisle}</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {aisle.sections.map((section) => (
                                <div key={section.id} className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter mb-2 block">Section {section.id}</span>
                                    <div className="flex flex-wrap gap-2">
                                        {section.bins.map((bin) => (
                                            <button
                                                key={bin}
                                                onClick={() => setSelectedBin(bin)}
                                                className={`w-12 h-12 flex items-center justify-center text-[10px] font-bold rounded-lg border transition-all ${selectedBin === bin
                                                    ? "bg-navy-900 text-white border-navy-900 scale-105 shadow-md"
                                                    : "bg-white text-slate-600 border-slate-200 hover:border-navy-900/30"
                                                    }`}
                                            >
                                                {bin.split('-').pop()}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            {selectedBin && (
                <div className="mt-8 bg-white p-6 rounded-2xl border border-navy-900/10 shadow-xl modal-animate">
                    <div className="flex justify-between items-center mb-6">
                        <div>
                            <h4 className="text-lg font-bold text-navy-900">Bin: {selectedBin}</h4>
                            <p className="text-sm text-slate-500">Inventory breakdown for this location</p>
                        </div>
                        <button onClick={() => setSelectedBin(null)} className="text-slate-400 hover:text-navy-900 font-bold text-xl">&times;</button>
                    </div>
                    <table className="w-full text-left">
                        <thead>
                            <tr className="text-[10px] font-bold text-slate-400 uppercase tracking-wider border-b border-slate-100">
                                <th className="pb-3">SKU</th>
                                <th className="pb-3 text-right">Qty</th>
                                <th className="pb-3 text-right">Last Moved</th>
                                <th className="pb-3"></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                                <td className="py-4 font-bold text-navy-900">TGL-882-BLUE</td>
                                <td className="py-4 text-right font-mono">140</td>
                                <td className="py-4 text-right text-slate-500 text-sm">2 days ago</td>
                                <td className="py-4 text-right">
                                    <button className="p-1.5 hover:bg-slate-200 rounded-lg text-slate-400"><MoreHorizontal size={16} /></button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );

    const renderSuspense = () => (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
            <div className="p-6 border-b border-slate-100 bg-slate-50/50 flex justify-between items-center">
                <div>
                    <h3 className="text-xl font-bold text-navy-900 font-serif">Suspense Area</h3>
                    <p className="text-sm text-slate-500">Items received but not yet put-away into a fixed bin.</p>
                </div>
                <div className="bg-amber-100 text-amber-700 px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1.5">
                    <AlertCircle size={14} /> 2 Items Pending
                </div>
            </div>
            <div className="divide-y divide-slate-100 text-sm">
                {suspenseItems.map((item) => (
                    <div key={item.id} className="p-4 flex items-center justify-between hover:bg-slate-50 transition-colors">
                        <div className="flex items-center gap-4">
                            <div className="w-10 h-10 rounded-lg bg-orange-50 text-orange-600 flex items-center justify-center font-bold">
                                <Box size={20} />
                            </div>
                            <div>
                                <div className="font-bold text-navy-900">{item.sku}</div>
                                <div className="text-xs text-slate-400">Ref: {item.id} • {item.client}</div>
                            </div>
                        </div>
                        <div className="flex items-center gap-12">
                            <div className="text-right">
                                <div className="font-bold text-navy-900">{item.qty} units</div>
                                <div className="text-xs text-slate-400">Rec: {item.receivedAt}</div>
                            </div>
                            <button className="bg-navy-900 text-white px-4 py-2 rounded-lg font-bold flex items-center gap-2 hover:bg-navy-800 transition-all text-xs shadow-md">
                                Put Away <ArrowRightLeft size={14} />
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );

    return (
        <div className="max-w-7xl mx-auto py-8 px-4">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
                <div>
                    <h1 className="text-3xl font-bold text-navy-900 font-serif">WMS: Warehouse Management</h1>
                    <p className="text-slate-500 mt-1">Manage physical locations, bin allocation, and put-away workflows.</p>
                </div>
                <div className="flex gap-3">
                    <button className="px-4 py-2 bg-white border border-slate-300 text-navy-900 rounded-lg hover:bg-slate-50 font-bold flex items-center gap-2 transition-all shadow-sm">
                        <Plus size={18} /> New Location
                    </button>
                    <button className="px-4 py-2 bg-navy-900 text-white rounded-lg hover:bg-navy-800 font-bold flex items-center gap-2 transition-all shadow-lg shadow-navy-900/20">
                        <Layout size={18} /> Print Labels
                    </button>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 bg-slate-100 p-1.5 rounded-xl mb-8 w-fit shadow-inner">
                {[
                    { id: "map", label: "Warehouse Map", icon: MapIcon },
                    { id: "suspense", label: "Suspense Area", icon: AlertCircle },
                    { id: "locations", label: "Location List", icon: Layout },
                ].map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`px-5 py-2.5 rounded-lg text-sm font-bold flex items-center gap-2 transition-all ${activeTab === tab.id ? "bg-white text-navy-900 shadow-md" : "text-slate-500 hover:text-navy-700"
                            }`}
                    >
                        <tab.icon size={16} /> {tab.label}
                    </button>
                ))}
            </div>

            {/* Main Content */}
            <div className="animate-in fade-in slide-in-from-bottom-2 duration-500">
                {activeTab === "map" && renderWarehouseMap()}
                {activeTab === "suspense" && renderSuspense()}
                {activeTab === "locations" && (
                    <div className="text-center py-20 bg-white rounded-2xl border border-dashed border-slate-200">
                        <Layout size={40} className="mx-auto text-slate-300 mb-4" />
                        <h3 className="text-lg font-bold text-navy-900">Location List Module</h3>
                        <p className="text-slate-400">Table view of all locations is under construction.</p>
                    </div>
                )}
            </div>
        </div>
    );
}
